<template>
  <HrPageLayout
    title="My Performance Improvement Plans"
    subtitle="Review your active PIPs and complete weekly progress sign-offs"
    icon="bx bx-task"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/employee-dashboard' },
      { label: 'My PIP' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline hr-btn-secondary" @click="loadPips">
        <i class="bx bx-refresh"></i> Refresh
      </button>
    </template>

    <div class="my-pip-page">
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-list-ul"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total PIPs</p>
          <h2 class="hr-metric-value">{{ metrics.total || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Active</p>
          <h2 class="hr-metric-value">{{ metrics.active || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-edit"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Pending sign-offs</p>
          <h2 class="hr-metric-value">{{ metrics.pendingSignoffs || 0 }}</h2>
            </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body hr-py-3">
          <div class="pip-filters">
            <div class="hr-filter-group">
              <label class="hr-form-label">Status</label>
              <select v-model="filterStatus" class="hr-select" @change="loadPips">
                <option value="">All</option>
                <option value="Active">Active</option>
                <option value="Completed">Completed</option>
                <option value="Terminated">Terminated</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading" class="hr-loading-wrap hr-py-5">
        <div class="hr-spinner"></div>
        <p class="hr-text-muted hr-mt-2">Loading your PIPs...</p>
      </div>

      <div v-else-if="!pips.length" class="hr-chart-card hr-text-center hr-py-5">
        <i class="bx bx-check-circle" style="font-size: 48px; color: #10b981;"></i>
        <h5 class="hr-mt-4">No PIPs on record</h5>
        <p class="hr-text-muted">You do not have any Performance Improvement Plans assigned.</p>
      </div>

      <div v-else class="hr-chart-card">
        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Manager</th>
                  <th>Period</th>
                  <th>Status</th>
                  <th class="hr-text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in pips" :key="p.id">
                  <td>{{ p.manager_name || '—' }}</td>
                  <td>{{ formatDate(p.start_date) }} – {{ formatDate(p.end_date) }}</td>
                  <td>
                    <span class="hr-badge" :class="statusClass(p.status)">{{ p.status }}</span>
                  </td>
                  <td class="hr-text-end">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="viewPip(p)">
                      <i class="bx bx-show"></i> View & sign
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <ModalWrapper ref="detailModal" :title="detailTitle" size="large">
      <template #body>
        <div v-if="loadingDetail" class="hr-loading-wrap hr-py-4">
          <div class="hr-spinner"></div>
        </div>
        <div v-else-if="selectedPip" class="pip-detail">
          <div class="pip-detail-header">
            <div>
              <p class="hr-text-muted hr-small hr-mb-1">Supervisor</p>
              <p class="pip-manager">{{ selectedPip.manager_name || '—' }}</p>
              <p class="hr-text-muted hr-small hr-mt-2">Period</p>
              <p class="pip-period">
                {{ formatDate(selectedPip.start_date) }} – {{ formatDate(selectedPip.end_date) }}
              </p>
            </div>
            <span class="hr-badge" :class="statusClass(selectedPip.status)">{{ selectedPip.status }}</span>
          </div>

          <div v-if="selectedPip.reason || selectedPip.goals" class="pip-goals-box hr-mt-3">
            <p class="hr-form-label">Improvement plan</p>
            <p class="pip-goals-text">{{ selectedPip.reason || selectedPip.goals }}</p>
          </div>

          <h4 class="pip-section-title hr-mt-4">Weekly sign-offs</h4>
          <p class="hr-text-muted hr-small hr-mb-2">
            Sign each week to confirm progress. Your manager will sign after you.
          </p>
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-compact">
              <thead>
                <tr>
                  <th>Week</th>
                  <th>Your status</th>
                  <th>Manager</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in signoffs" :key="weekKey(s)">
                  <td>{{ formatDate(weekStart(s)) }} – {{ formatDate(weekEnd(s)) }}</td>
                  <td>
                    <span v-if="s.employee_signed_at" class="pip-signed">
                      <i class="bx bx-check-circle"></i> Signed {{ formatDateTime(s.employee_signed_at) }}
                    </span>
                    <span v-else class="hr-badge hr-badge-warning">Action needed</span>
                  </td>
                  <td>
                    <span v-if="s.manager_signed_at" class="pip-signed">Signed</span>
                    <span v-else class="hr-text-muted">Awaiting manager</span>
                  </td>
                  <td class="hr-text-end">
                    <button
                      v-if="selectedPip.status === 'Active' && !s.employee_signed_at"
                      type="button"
                      class="hr-btn hr-btn-sm hr-btn-success"
                      @click="openSignoffModal(s)"
                    >
                      Sign week
                    </button>
                    <button
                      v-else-if="selectedPip.status === 'Active'"
                      type="button"
                      class="hr-btn hr-btn-sm hr-btn-outline hr-btn-secondary"
                      @click="openSignoffModal(s)"
                    >
                      View
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.detailModal.close()">Close</button>
      </template>
    </ModalWrapper>

    <ModalWrapper ref="signoffModal" :title="signoffTitle" size="medium">
      <template #body>
        <div class="hr-filter-group">
          <label class="hr-form-label">Progress notes <span class="pip-required">*</span></label>
          <textarea
            v-model="signoffForm.progressNotes"
            class="hr-textarea"
            rows="4"
            placeholder="Describe progress, challenges, and support received this week"
          ></textarea>
        </div>
        <div class="hr-filter-group hr-mt-2">
          <label class="hr-form-label">Comments (optional)</label>
          <textarea v-model="signoffForm.comments" class="hr-textarea" rows="2"></textarea>
        </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.signoffModal.close()">Cancel</button>
        <button type="button" class="hr-btn hr-btn-success" :disabled="signing" @click="submitSignoff">
          <span v-if="signing" class="hr-spinner small"></span>
          Submit sign-off
        </button>
      </template>
    </ModalWrapper>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute, toast } from "@/api";

export default {
  name: "MyWarnings",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      loading: true,
      loadingDetail: false,
      signing: false,
      filterStatus: "",
      pips: [],
      metrics: {},
      selectedPip: null,
      signoffs: [],
      signoffForm: {
        week_start: "",
        progressNotes: "",
        comments: ""
      }
    };
  },
  computed: {
    detailTitle() {
      return this.selectedPip ? "PIP details" : "PIP";
    },
    signoffTitle() {
      return this.signoffForm.week_start
        ? `Sign week of ${this.formatDate(this.signoffForm.week_start)}`
        : "Weekly sign-off";
    }
  },
  mounted() {
    this.loadPips();
  },
  methods: {
    async loadPips() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getMyPips");
        if (this.filterStatus) data.append("status", this.filterStatus);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.pips = res.data.data?.pips ?? [];
          this.metrics = res.data.data?.metrics ?? {};
        } else {
          throw new Error(res?.data?.message || "Failed to load");
        }
      } catch (e) {
        toast.error(e.message || "Failed to load PIPs");
        this.pips = [];
      } finally {
        this.loading = false;
      }
    },
    async viewPip(p) {
      this.selectedPip = p;
      this.loadingDetail = true;
      this.$refs.detailModal.open();
      try {
        const data = new FormData();
        data.append("function", "getPipDetail");
        data.append("id", p.id);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.selectedPip = res.data.data?.pip ?? p;
          this.signoffs = res.data.data?.signoffs ?? [];
        }
      } catch (e) {
        toast.error("Failed to load PIP details");
      } finally {
        this.loadingDetail = false;
      }
    },
    weekKey(s) {
      return this.weekStart(s);
    },
    weekStart(s) {
      return s.week_start_date || s.week_start;
    },
    weekEnd(s) {
      return s.week_end_date || s.week_end;
    },
    openSignoffModal(s) {
      this.signoffForm = {
        week_start: this.weekStart(s),
        progressNotes: s.progress_notes || "",
        comments: s.employee_comments || ""
      };
      this.$refs.signoffModal.open();
    },
    async submitSignoff() {
      if (!(this.signoffForm.progressNotes || "").trim()) {
        toast.warning("Please add progress notes before signing.");
        return;
      }
      this.signing = true;
      try {
        const data = new FormData();
        data.append("function", "addOrUpdatePipWeeklySignoff");
        data.append("pipId", this.selectedPip.id);
        data.append("weekStartDate", this.signoffForm.week_start);
        data.append("progressNotes", this.signoffForm.progressNotes);
        data.append("signedAs", "employee");
        data.append("employeeComments", this.signoffForm.comments || "");
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          toast.success("Week signed successfully");
          this.$refs.signoffModal.close();
          await this.viewPip(this.selectedPip);
          this.loadPips();
        } else {
          throw new Error(res?.data?.message || "Sign-off failed");
        }
      } catch (e) {
        toast.error(e.message || "Failed to save sign-off");
      } finally {
        this.signing = false;
      }
    },
    formatDate(d) {
      if (!d) return "—";
      const date = new Date(d);
      return Number.isNaN(date.getTime())
        ? d
        : date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    },
    formatDateTime(d) {
      if (!d) return "—";
      const date = new Date(d);
      return date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    statusClass(status) {
      const map = {
        Active: "hr-badge-warning",
        Completed: "hr-badge-success",
        Terminated: "hr-badge-danger",
        Withdrawn: "hr-badge-secondary"
      };
      return map[status] || "hr-badge-secondary";
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.pip-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.pip-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.pip-manager,
.pip-period {
  font-weight: 600;
  margin: 0;
}

.pip-goals-box {
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.pip-goals-text {
  margin: 0;
  white-space: pre-wrap;
  line-height: 1.5;
}

.pip-section-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 8px;
}

.pip-signed {
  color: #059669;
  font-size: 12px;
}

.pip-required {
  color: #dc2626;
}

.hr-table-compact td,
.hr-table-compact th {
  font-size: 13px;
  padding: 10px 12px;
}

.hr-text-end {
  text-align: right;
}
</style>
